/*
  VRO size memory.

  Remembers the clothing size a shopper last chose anywhere in the shop, so
  the fashion PDP can say "Recommended Size is L" with their own answer
  behind it. Loaded on every page (layout/theme.liquid), after
  vro-personalization.js, whose per-shopper profile it writes into:
  guests -> localStorage, logged-in shoppers -> their customer id (and the
  server copy, when that sync is configured). Nothing new is invented for
  storage; this is one more facet, `size_memory`, on the same profile.

  Where a size is picked up from — the size quizzes only, and a Retake
  is the only thing that replaces one:
    - the size quiz on the Fashion Week rail (.size-quiz-btn)
    - the product page's size quiz (assets/vro-pdp-quiz.js)

  Picking a size on a product page (its chips, or quick-add) is choosing
  a variant, and ticking a Size filter is a search; neither is an answer,
  and neither is kept: it used to
  come back as "Recommended Size is 36 / Because you wear a 36 in
  Silisoul" for a shopper who had never taken the quiz. Only the size
  quiz recommends a size, and only its answers carry a brand.

  Sizes are kept per kind, because a waist size and a letter size answer
  different questions: `alpha` (S, M, L, XL, 2XL…) and `numeric` (28, 30,
  32… waist). A product is recommended the size of its own kind — the same
  brand's size first, otherwise the most recent pick.
*/
(function () {
  'use strict';
  if (window.VroSizeMemory) return;

  var FACET = 'size_memory';
  var LOCAL = 'vro_size_memory_v1';

  function engine() {
    return (window.VroPersonalization && window.VroPersonalization.profile) || null;
  }

  function norm(v) {
    return String(v == null ? '' : v).trim().toUpperCase().replace(/\s+/g, ' ');
  }

  function kindOf(v) {
    v = norm(v).replace(/^SIZE\s*/, '');
    if (!v) return null;
    if (/^\d{2}$/.test(v)) {
      var n = parseInt(v, 10);
      return n >= 24 && n <= 50 ? 'numeric' : null;
    }
    if (/^(X{0,4}S|M|X{0,4}L|[2-6]XL|[2-6]XS|XXXL|FREE ?SIZE|ONE ?SIZE)$/.test(v)) return 'alpha';
    return null;
  }

  function clone(o) {
    try { return JSON.parse(JSON.stringify(o || {})); } catch (e) { return {}; }
  }

  function read() {
    var p = engine();
    var m = null;
    if (p && p.getFacet) {
      m = p.getFacet(FACET);
    } else {
      try { m = JSON.parse(localStorage.getItem(LOCAL) || 'null'); } catch (e) { m = null; }
    }
    return (m && typeof m === 'object') ? m : {};
  }

  function write(m) {
    var p = engine();
    if (p && p.setFacet) { p.setFacet(FACET, m); return; }
    try { localStorage.setItem(LOCAL, JSON.stringify(m)); } catch (e) { /* private mode */ }
  }

  function remember(value, source, brand) {
    var v = norm(value).replace(/^SIZE\s*/, '');
    var kind = kindOf(v);
    if (!kind) return;
    var m = clone(read());
    brand = String(brand || '').trim();
    m[kind] = { value: v, source: source || '', brand: brand, at: Date.now() };
    if (brand && source === 'quiz') {
      var key = brand.toLowerCase();
      m.brands = m.brands || {};
      m.brands[key] = m.brands[key] || {};
      m.brands[key][kind] = v;
      m.brands[key].name = brand;
      m.brands[key].quiz = 1;
    }
    write(m);
  }

  /* Sizes saved from anything but the quiz before that stopped (size
     taps, quick-add, size filters, and brand entries without the quiz
     mark) are dropped, so neither the product page nor the profile's
     memories go on repeating them. Quiz answers are kept. */
  function dropTapped() {
    var m = read();
    if (!m || typeof m !== 'object') return;
    var changed = false;
    ['alpha', 'numeric'].forEach(function (k) {
      if (m[k] && m[k].source !== 'quiz') { delete m[k]; changed = true; }
    });
    if (m.brands) {
      Object.keys(m.brands).forEach(function (b) {
        if (!m.brands[b] || !m.brands[b].quiz) { delete m.brands[b]; changed = true; }
      });
    }
    if (changed) write(clone(m));
  }

  /* The size quiz's own facet ('size', or 'size@<row>') predates this file;
     read it too, so an answer given before this shipped still counts. */
  function legacyQuizSize() {
    var p = engine();
    if (!p || !p.get) return null;
    var facets = (p.get() || {}).facets || {};
    var found = null;
    Object.keys(facets).forEach(function (k) {
      if (k === 'size' || k.indexOf('size@') === 0) {
        if (typeof facets[k] === 'string' && !found) found = facets[k];
      }
    });
    return found;
  }

  /* Letters and numbers for the same garment.

     A shirt or jacket numbered 38-46 is numbered by the chest, and on that
     scale a medium is a 40. A trouser numbered 30-38 is numbered by the
     waist, where a medium is a 32 — the same letter, a different number,
     and the two runs overlap. So this table is only ever read where the
     number can be a chest measurement; see chestLike() below. */
  var ALPHA_CHEST = { XS: 36, S: 38, M: 40, L: 42, XL: 44, XXL: 46, '2XL': 46, XXXL: 48, '3XL': 48 };
  var CHEST_ALPHA = { 36: 'XS', 38: 'S', 40: 'M', 42: 'L', 44: 'XL', 46: 'XXL', 48: 'XXXL' };

  /* The same for a waist, and read in one direction only.

     The note above is about reading a NUMBER as a letter: 32 is a medium
     waist and a 32 chest is nothing, so a remembered number cannot name a
     letter unless the number is a chest. Reading a LETTER as a waist has
     no such problem - a medium is a 32 waist, on a product we already know
     is worn on the lower half.

     It matters now because the quiz asks in letters whatever the product
     is numbered in. Before that a trouser was asked for a number and the
     answer needed no conversion; since it isn't, a shopper who answered
     "XL" got no recommendation on any bottom at all. */
  var ALPHA_WAIST = { XS: 28, S: 30, M: 32, L: 34, XL: 36, XXL: 38, '2XL': 38, XXXL: 40, '3XL': 40 };

  /* Waist runs start at 26 and rarely reach 38; chest runs start at 36 and
     go up. A product whose smallest number is 38 or more is being numbered
     by the chest whatever it is called, which is how an untyped product
     still gets an answer. Below that we want to be told. */
  function chestLike(numbers, garment) {
    if (garment === 'bottom') return false;
    if (garment === 'top') return true;
    if (!numbers.length) return false;
    return Math.min.apply(null, numbers) >= 38;
  }

  /* sizes: the product's own size values, as written on the product.
     opts.garment: 'top', 'bottom', or '' when the product does not say.
     Returns { value, source, brand, how } with value spelled as the
     product spells it, or null. `how` is 'exact', 'converted' (their
     letter read as this product's number, or the other way about) or
     'nearest' (the closest number this product comes in). */
  function recommend(sizes, brand, opts) {
    sizes = (sizes || []).filter(Boolean);
    if (!sizes.length) return null;
    var garment = (opts && opts.garment) || '';
    var normed = sizes.map(norm);
    var kinds = [];
    var numbers = [];
    normed.forEach(function (s) {
      var k = kindOf(s);
      if (k && kinds.indexOf(k) === -1) kinds.push(k);
      if (k === 'numeric') numbers.push(parseInt(s, 10));
    });
    if (!kinds.length) return null;
    var chest = chestLike(numbers, garment);

    function onProduct(v) {
      var i = normed.indexOf(norm(v));
      return i > -1 ? sizes[i] : null;
    }

    /* Their size, spelled the way this product spells sizes. Only where
       the number in play can be a chest measurement, in either direction:
       a remembered 32 is a waist and names no letter. */
    function converted(v) {
      var n = norm(v);
      var k = kindOf(n);
      if (!chest) {
        /* Only where the product has actually said it is a bottom. An
           untyped product whose numbers are small is not assumed to be
           one: guessing wrong here puts a waist on a shirt. */
        if (garment === 'bottom' && k === 'alpha' && ALPHA_WAIST[n]) {
          return onProduct(String(ALPHA_WAIST[n]));
        }
        return null;
      }
      if (k === 'alpha' && ALPHA_CHEST[n]) return onProduct(String(ALPHA_CHEST[n]));
      if (k === 'numeric' && CHEST_ALPHA[parseInt(n, 10)]) return onProduct(CHEST_ALPHA[parseInt(n, 10)]);
      return null;
    }

    /* The closest number this product does come in, when it does not come
       in theirs. Two sizes at the outside — beyond that it is a different
       garment, not a near miss — and a tie goes up, which is the way a
       shirt is forgiving. */
    function nearest(v) {
      var n = norm(v);
      if (kindOf(n) !== 'numeric' || !numbers.length) return null;
      var want = parseInt(n, 10);
      var best = null;
      numbers.forEach(function (num) {
        var d = Math.abs(num - want);
        if (d === 0 || d > 2) return;
        if (!best || d < best.d || (d === best.d && num > best.num)) best = { num: num, d: d };
      });
      return best ? onProduct(String(best.num)) : null;
    }

    /* Each way of reading their size, tried across every source before the
       next one is: an exact match anywhere beats a conversion anywhere,
       and a conversion beats a near miss. */
    var ways = [
      { how: 'exact', find: onProduct },
      { how: 'converted', find: converted },
      { how: 'nearest', find: nearest }
    ];

    var m = read();
    var b = brand && m.brands ? m.brands[String(brand).trim().toLowerCase()] : null;
    var legacy = legacyQuizSize();

    for (var w = 0; w < ways.length; w++) {
      var way = ways[w];

      if (b && b.quiz) {
        for (var i = 0; i < kinds.length; i++) {
          var hit = b[kinds[i]] && way.find(b[kinds[i]]);
          if (hit) return { value: hit, source: 'brand', brand: b.name || brand, how: way.how, said: norm(b[kinds[i]]) };
        }
      }

      var best = null;
      ['numeric', 'alpha'].forEach(function (k) {
        var e = m[k];
        /* Only a quiz answer recommends a size; a filter ticked while
           browsing is not the shopper telling us what they wear. */
        if (e && e.source === 'quiz' && way.find(e.value) && (!best || (e.at || 0) > (best.at || 0))) best = e;
      });
      if (best) {
        return { value: way.find(best.value), source: best.source, brand: best.brand, how: way.how, said: norm(best.value) };
      }

      if (legacy && way.find(legacy)) {
        return { value: way.find(legacy), source: 'quiz', brand: '', how: way.how, said: norm(legacy) };
      }
    }
    return null;
  }

  /* A size ticked in a listing's Size filter (or already in its URL) used
     to be saved too. It is a search, not an answer: only the size quiz
     and its Retake write a size now. */

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.size-quiz-btn') : null;
    if (!btn) return;
    /* custom.js flips aria-pressed in its own handler; read it after. */
    setTimeout(function () {
      if (btn.getAttribute('aria-pressed') === 'true') {
        remember(btn.getAttribute('data-size-value'), 'quiz', '');
      }
    }, 0);
  });

  window.VroSizeMemory = { remember: remember, recommend: recommend, read: read, kindOf: kindOf };

  function start() { dropTapped(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
